import type { TableSchema } from "@/hooks/useMcpSchema";

export type PlaceholderDisplayType =
  | "text"
  | "number"
  | "date"
  | "datetime-local"
  | "dropdown"
  | "multiselect"
  | "autocomplete";

export interface PlaceholderInfo {
  name: string;
  columnName: string | null;
  columnType: string | null;
  inputType: "text" | "number" | "date" | "datetime-local";
  /** Explicit display type from {{name|type}} syntax. Overrides inputType for rendering. */
  displayType: PlaceholderDisplayType;
  /** Resolved default value (from {{name|type|default}} syntax). null if none. */
  defaultValue: string | null;
}

/**
 * Regex to match {{name}}, {{name|type}}, or {{name|type|default}}.
 * Name can contain word chars and spaces. Type is optional (word chars + hyphen).
 * Default value is optional and can contain anything except }}.
 */
const PLACEHOLDER_REGEX = /\{\{([\w ]+?)(?:\|([\w-]+))?(?:\|([^}]*?))?\}\}/g;

/** Extract unique placeholder names from a SQL string. Placeholders use {{name}} or {{name|type}} syntax. */
export function extractPlaceholders(sql: string): string[] {
  const regex = new RegExp(PLACEHOLDER_REGEX.source, "g");
  const seen = new Set<string>();
  const result: string[] = [];
  let match;
  while ((match = regex.exec(sql)) !== null) {
    const name = match[1].trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      result.push(name);
    }
  }
  return result;
}

/** Extract the explicit type hint for a placeholder, if present. */
export function extractPlaceholderType(sql: string, placeholderName: string): string | null {
  const regex = new RegExp(PLACEHOLDER_REGEX.source, "g");
  let match;
  while ((match = regex.exec(sql)) !== null) {
    if (match[1].trim() === placeholderName && match[2]) {
      return match[2].toLowerCase();
    }
  }
  return null;
}

/** Extract the raw default value string for a placeholder, if present. */
function extractPlaceholderDefault(sql: string, placeholderName: string): string | null {
  const regex = new RegExp(PLACEHOLDER_REGEX.source, "g");
  let match;
  while ((match = regex.exec(sql)) !== null) {
    if (match[1].trim() === placeholderName && match[3] !== undefined) {
      return match[3];
    }
  }
  return null;
}

/** Resolve special default value keywords into actual values. */
function resolveDefaultValue(raw: string, displayType: PlaceholderDisplayType): string {
  const lower = raw.trim().toLowerCase();

  if (displayType === "date" || displayType === "datetime-local") {
    const now = new Date();
    if (lower === "now" || lower === "today") {
      if (displayType === "datetime-local") {
        // yyyy-MM-ddTHH:mm format for datetime-local input
        return now.toISOString().slice(0, 16);
      }
      // yyyy-MM-dd for date input
      return now.toISOString().slice(0, 10);
    }
    if (lower === "yesterday") {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    }
    if (lower === "tomorrow") {
      const d = new Date(now);
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    }
  }

  // Return raw value as-is for everything else
  return raw.trim();
}

const VALID_DISPLAY_TYPES = new Set<PlaceholderDisplayType>([
  "text", "number", "date", "datetime-local", "dropdown", "multiselect", "autocomplete",
]);

function parseDisplayType(raw: string | null): PlaceholderDisplayType | null {
  if (!raw) return null;
  const t = raw.toLowerCase();
  if (VALID_DISPLAY_TYPES.has(t as PlaceholderDisplayType)) return t as PlaceholderDisplayType;
  return null;
}

/** Map a database column type string to an HTML input type. */
function mapColumnTypeToInputType(columnType: string): PlaceholderInfo["inputType"] {
  const t = columnType.toLowerCase();
  if (/int|serial|numeric|decimal|float|double|real|number/.test(t)) return "number";
  if (/^date$/.test(t)) return "date";
  if (/timestamp|datetime/.test(t)) return "datetime-local";
  return "text";
}

/**
 * Infer placeholder metadata by inspecting the SQL context around the placeholder
 * and looking up the compared column's type in the schema.
 */
export function inferPlaceholderInfo(
  sql: string,
  placeholderName: string,
  schemaData: TableSchema[],
): PlaceholderInfo {
  // Try to find the column this placeholder is compared against
  const escaped = placeholderName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Allow optional spaces, optional |type, and optional |default inside {{ }}
  const phPattern = `\\{\\{\\s*${escaped}(?:\\|[\\w-]+)?(?:\\|[^}]*?)?\\s*\\}\\}`;
  const patterns = [
    new RegExp(`"?([\\w]+(?:\\.[\\w]+)?)"?\\s*(?:=|!=|<>|>|<|>=|<=|LIKE|ILIKE|IN)\\s*\\(?\\s*${phPattern}`, "i"),
    new RegExp(`${phPattern}\\s*\\)?\\s*(?:=|!=|<>|>|<|>=|<=|LIKE|ILIKE|IN)\\s*"?([\\w]+(?:\\.[\\w]+)?)"?`, "i"),
  ];

  let columnName: string | null = null;
  for (const pattern of patterns) {
    const match = sql.match(pattern);
    if (match) {
      columnName = match[1];
      break;
    }
  }

  // Look up column type from schema
  let columnType: string | null = null;
  if (columnName) {
    const parts = columnName.split(".");
    const colNameOnly = parts.length > 1 ? parts[1] : parts[0];
    const tableName = parts.length > 1 ? parts[0] : null;

    for (const table of schemaData) {
      if (tableName && table.table_name !== tableName) continue;
      const col = table.columns.find(
        (c) => c.column_name.toLowerCase() === colNameOnly.toLowerCase(),
      );
      if (col) {
        columnType = col.data_type;
        break;
      }
    }
  }

  const inputType = columnType ? mapColumnTypeToInputType(columnType) : "text";
  const explicitType = parseDisplayType(extractPlaceholderType(sql, placeholderName));
  const displayType = explicitType ?? inputType;

  // Resolve default value
  const rawDefault = extractPlaceholderDefault(sql, placeholderName);
  const defaultValue = rawDefault !== null ? resolveDefaultValue(rawDefault, displayType) : null;

  return {
    name: placeholderName,
    columnName,
    columnType,
    inputType,
    displayType,
    defaultValue,
  };
}

/**
 * Replace all {{placeholder}} / {{placeholder|type}} tokens in the SQL with values.
 * For multiselect, the value is a comma-separated string that gets expanded to an IN list.
 * Values are properly quoted for SQL.
 */
export function replacePlaceholders(
  sql: string,
  values: Record<string, string>,
  placeholders: PlaceholderInfo[],
): string {
  return sql.replace(new RegExp(PLACEHOLDER_REGEX.source, "g"), (_, rawName) => {
    const name = rawName.trim();
    const val = values[name] ?? "";
    const info = placeholders.find((p) => p.name === name);

    // Multiselect: expand comma-separated values into an IN list
    if (info?.displayType === "multiselect" && val) {
      const items = val
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => `'${v.replace(/'/g, "''")}'`);
      return items.length > 0 ? items.join(", ") : "''";
    }

    // For numeric types, don't quote
    if (info?.inputType === "number" && info.displayType !== "dropdown" && val !== "") return val;
    // For everything else, quote with single quotes (escape internal quotes)
    return `'${val.replace(/'/g, "''")}'`;
  });
}

/** Build a SQL query to fetch unique values for a column. */
export function buildUniqueValuesQuery(
  tableName: string,
  columnName: string,
): string {
  return `SELECT DISTINCT \`${columnName}\` FROM \`${tableName}\` WHERE \`${columnName}\` IS NOT NULL ORDER BY \`${columnName}\` LIMIT 500`;
}

/** Try to extract the table name from a SQL query (simple FROM clause extraction). */
export function extractTableFromSql(sql: string): string | null {
  const match = sql.match(/\bFROM\s+["`]?(\w+)["`]?/i);
  return match ? match[1] : null;
}
